/* eslint-disable no-shadow */
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';

import config from 'config';
import {
    checkTariffLimit,
    cloneClient,
    createClient as createClientApi,
    exportClient as exportClientApi,
    importClient as importClientApi,
    updateCompany as updateCompanyApi
} from 'services/company/api';
import {
    addClient as addClientAction,
    updateClient as updateClientAction,
    updateMemberList as updateMemberListAction
} from 'services/company/action';
import {handleChange, toggleState} from 'utils/ComponentUtilFunctions';
import Bots from "./new/Bots";
import ConfirmationModal from "../../../../../components-new/modals/ConfirmationModal";
import {t} from "../../../../../components/Translate";
import CreateClientModal from "./new/modals/CreateClientModal";
import CloseChatsModal from "../../chat/chat-components/CloseChatsModal";

/*
 * What the server said went wrong, or the general line when it said nothing.
 *
 * The bot limit used to be assumed: any failure of the check below was reported
 * as a full tariff, so a company with no tariff at all, or a server that was
 * simply down, was told to delete a bot.
 */
const reasonFrom = (e) => {
    const data = e && e.response && e.response.data;
    return typeof data === 'string' && data ? data : 'somethingWentWrong';
};

class Client extends Component {
    constructor(props) {
        super(props);
        this.state = {
            isCreateClientModalOpen: false,
            currentPlan: {},
            updating: false,
            editingName: false,
            activeTabId: -1,
            showChat: {},
            isConfirmationDialogOpen: false,
            removingClient: {}
        };
        this.toggleState = toggleState.bind(this);
        this.handleChange = handleChange.bind(this);
    }

    /**
     * Guarded the same way updateClient is: the modal holds its own lock, but this
     * is the method every other caller reaches, and the server has no uniqueness on
     * a bot's name, so a second call would simply create a second bot.
     */
    createClient = (clientData) => {
        const {addClient, addNotification} = this.props;

        if (this.creating) return Promise.resolve();
        this.creating = true;

        clientData.domain = clientData.name;

        if (clientData.originalClientId) {
           return cloneClient(clientData, clientData.originalClientId)
                .then((newClient) => {
                    addClient(newClient);
                    this.setState({
                        isCreateClientModalOpen: false,
                    });
                    addNotification('botAddedSuccessfully', config.notificationLevels.success, {});
                })
                .catch((e) => {
                    addNotification(reasonFrom(e), config.notificationLevels.error, {});
                })
                .finally(() => {
                    this.creating = false;
                })
        } else {
            return createClientApi(clientData)
                .then((newClient) => {
                    addClient(newClient);
                    this.setState({
                        isCreateClientModalOpen: false,
                    });
                    addNotification('botAddedSuccessfully', config.notificationLevels.success, {});
                })
                .catch((e) => {
                    addNotification(reasonFrom(e), config.notificationLevels.error, {});
                })
                .finally(() => {
                    this.creating = false;
                })
        }

    };

    importClient = (name, exportedBot) => {
        const {addClient, addNotification} = this.props;
        return importClientApi(name, exportedBot)
            .then((newClient) => {
                addClient(newClient);
                this.setState({
                    isCreateClientModalOpen: false,
                });
                addNotification('botAddedSuccessfully', config.notificationLevels.success, {});
            })
            .catch((e) => {
                addNotification(reasonFrom(e), config.notificationLevels.error, {});
            });
    };

    exportClient = (client) => {
        const {addNotification} = this.props;
        return exportClientApi(client.id)
            .then((exportedBot) => {
                const blob = new Blob([JSON.stringify(exportedBot, null, 2)], {type: 'application/json'});
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `${client.name || 'bot'}-export.json`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            })
            .catch(() => {
                addNotification('somethingWentWrong', config.notificationLevels.error, {});
            });
    };

    closeCreateClientModal = () => {
        this.setState({
            isCreateClientModalOpen: false,
        });
    };

    openCreateClientModal = () => {
        const {addNotification} = this.props;
        checkTariffLimit()
            .then(() => {
                this.setState({
                    isCreateClientModalOpen: true,
                });
            })
            .catch((e) => {
                addNotification(reasonFrom(e), config.notificationLevels.error, {});
            })
    };

    /**
     * Deleting a bot takes down its chats, messages, attachments and stored
     * knowledge, which on a busy company is not instant - hence the dialog stays
     * open and locked until the request comes back.
     */
    handleRemoveClient = () => {
        this.updateClient({
            status: config.companyStatusMap.deleted,
        }, this.state.removingClient).then(() => {
            this.closeConfirmationDialog();
        });
    };

    openConfirmationDialog = (client) => {
        this.setState({isConfirmationDialogOpen: true})
        this.setState({removingClient: client})
    }

    closeConfirmationDialog = () => {
        this.setState({isConfirmationDialogOpen: false})
        this.setState({removingClient: {}})
    }

    updateClient = (updateData, client) => {
        const {updating} = this.state;
        if (updating) {
            return Promise.resolve();
        }

        this.setState({
            updating: true,
        });


        const {updateClientAction, addNotification} = this.props;
        const data = {
            name: client.name,
            image: client.image,
            domain: client.domain,
            nlpEnabled: client.nlpEnabled,
        };


        Object.assign(data, updateData);
        return updateCompanyApi(client.id, data)
            .then(() => {
                updateClientAction(client.id, updateData);
                if (updateData.status === config.companyStatusMap.deleted) {
                    addNotification('botRemovedSuccessfully', config.notificationLevels.success, {});
                }
            })
            .catch(() => {
                addNotification('somethingWentWrong', config.notificationLevels.error, {});
            })
            .finally(() => {
                this.setState({
                    updating: false,
                });
            });
    };

    handleImageChange = (client, imageLink) => {
        this.updateClient({
            image: imageLink,
        }, client);
    };

    render() {
        const {
            company, user, addNotification
        } = this.props;
        const {isCreateClientModalOpen, isConfirmationDialogOpen, updating} = this.state;
        const {subs: clients} = company;
        const {tariff} = company;


        return (
            <div style={{height: '100%'}}>
                <Bots
                    addNotification={addNotification}
                    clients={clients}
                    openCreateClientModal={this.openCreateClientModal}
                    handleRemoveClient={this.openConfirmationDialog}
                    createClient={this.createClient}
                    handleImageChange={this.handleImageChange}
                    exportClient={this.exportClient}
                    user={user}
                    tariff={tariff}
                />

                {isConfirmationDialogOpen &&
                    <ConfirmationModal
                        variant="delete"
                        isOpen={isConfirmationDialogOpen}
                        loading={updating}
                        onClose={this.closeConfirmationDialog}
                        onConfirm={this.handleRemoveClient}
                        message={t({
                            text: 'areYouSureYouWantToProceed',
                            user
                        })}
                    />
                }

                {isCreateClientModalOpen &&
                    <CreateClientModal
                        isOpen={isCreateClientModalOpen}
                        onRequestClose={this.closeCreateClientModal}
                        clients={clients}
                        tariff={tariff}
                        createClient={this.createClient}
                        importClient={this.importClient}
                        closeCreateClientModal={this.closeCreateClientModal}
                        user={user}
                    />
                }

            </div>
        );
    }
}

Client.propTypes = {
    company: PropTypes.object.isRequired,
    addNotification: PropTypes.func.isRequired,
    addClient: PropTypes.func.isRequired,
    updateClientAction: PropTypes.func.isRequired,
    updateMemberList: PropTypes.func.isRequired,
    commonStyles: PropTypes.shape({
        wrapper: PropTypes.object.isRequired,
        title: PropTypes.object.isRequired,
    }).isRequired,
    user: PropTypes.object.isRequired,
};

const mapDispatchToProps = dispatch => ({
    addClient: (newClient) => {
        dispatch(addClientAction(newClient));
    },
    updateMemberList: (membersUpdate) => {
        dispatch(updateMemberListAction(membersUpdate));
    },
    updateClientAction: (clientId, update) => {
        dispatch(updateClientAction(clientId, update));
    },
});

export default connect(null, mapDispatchToProps)(Client);



// WEBPACK FOOTER //
// ./src/scenes/dashboard/components/management/clients/index.jsx